import React from 'react';
import { Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import {
  AgroOverviewLocation,
  AgroOverviewSummary,
  RiskLevel,
} from 'types/Meteorology/Agro/Overview/Dashboard';
import { RISK_LABEL } from 'helper/Meteorology/Agro/Overview/locationsTable';
import { localidadeComUf } from 'helper/Meteorology/Agro/Overview';
import {
  CloudRainIcon,
  DropIcon,
  ShieldPlusIcon,
} from 'components/Meteorology/Agro/Overview/Dashboard/icons';

import './index.scss';

// Mesmas cores dos badges de risco do desktop (SummaryCards/index.scss).
const RISK_CLASS: Record<RiskLevel, string> = {
  baixo: 'risk-low',
  moderado: 'risk-mid',
  alto: 'risk-high',
  muito_alto: 'risk-very-high',
};

const EMPTY_HINT = 'sem local cadastrado';

// Estado vazio (§4): o VALOR dos cards LOCAIS (3-4) vira link de cadastro.
const registerLink = (
  <Link to="/agro/register" className="summary-card-register-link">
    Cadastre um local
    <FontAwesomeIcon
      icon={faArrowUpRightFromSquare}
      className="summary-card-register-icon"
    />
  </Link>
);

function KpiTile({
  label, value, hint, icon, iconClass, valueClassName,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
  icon: React.ReactNode;
  iconClass: string;
  valueClassName?: string;
}) {
  return (
    <div className="agro-overview-mobile-kpi">
      <span className={`summary-icon-circle ${iconClass}`}>{icon}</span>
      <div className="agro-overview-mobile-kpi-body">
        <div className="agro-overview-mobile-kpi-label">{label}</div>
        <div className={`agro-overview-mobile-kpi-value ${valueClassName ?? ''}`}>{value}</div>
        <div className="agro-overview-mobile-kpi-hint">{hint}</div>
      </div>
    </div>
  );
}

export default function SummaryCardsMobile({
  summary,
  locations,
  loading,
  isEmptyState,
}: {
  summary: AgroOverviewSummary | undefined;
  locations: AgroOverviewLocation[];
  loading: boolean;
  isEmptyState: boolean;
}) {
  const loadingValue = <Spinner animation="border" size="sm" />;

  const risco = summary?.riscoEstresseHidrico ?? null;
  const localidade = summary?.maiorAcumuladoChuva.localidade ?? null;

  // ADENDO-1 ao §4: cards 1-2 são da grade nacional (Brasil) e exibem valor
  // mesmo no estado vazio; o CTA "Cadastre um local" fica nos cards 3-4.
  const globalCardValue = (value: React.ReactNode) => (loading ? loadingValue : value);
  const cardValue = (value: React.ReactNode) => (
    isEmptyState ? registerLink : globalCardValue(value)
  );
  const cardHint = (hint: string) => (isEmptyState ? EMPTY_HINT : hint);

  const chuvaHint = localidade
    ? `Últimos 7 dias · Em ${localidadeComUf(localidade, locations)}`
    : 'Últimos 7 dias';

  return (
    <div className="agro-overview-mobile-kpis">
      <KpiTile
        label="ÁREA EM EXCEDENTE HÍDRICO"
        value={globalCardValue(summary ? `${summary.excedenteHidricoPct}%` : '—')}
        hint="Brasil — últimos 7 dias"
        iconClass="icon-excedente"
        icon={<DropIcon title="Excedente hídrico" />}
      />
      <KpiTile
        label="ÁREA EM ESTRESSE HÍDRICO"
        value={globalCardValue(summary ? `${summary.estresseHidricoPct}%` : '—')}
        hint="Brasil — últimos 7 dias"
        iconClass="icon-estresse"
        icon={<DropIcon title="Estresse hídrico" />}
      />
      <KpiTile
        label="MAIOR ACUMULADO DE CHUVA"
        value={cardValue(summary ? `${summary.maiorAcumuladoChuva.mm} mm` : '—')}
        hint={cardHint(chuvaHint)}
        iconClass="icon-chuva"
        icon={<CloudRainIcon title="Chuva acumulada" />}
      />
      <KpiTile
        label="RISCO ESTRESSE HÍDRICO"
        value={cardValue(risco ? RISK_LABEL[risco] : '—')}
        /* Único card de PREVISÃO da linha: o valor vem de
           `risco_estresse_hidrico_7d`. Os outros três são observados. */
        hint={cardHint('Próximos 7 dias')}
        iconClass="icon-risco"
        icon={<ShieldPlusIcon title="Risco" />}
        valueClassName={!isEmptyState && risco ? RISK_CLASS[risco] : undefined}
      />
    </div>
  );
}
